import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PROVIDERS, OAUTH_REDIRECT_URI } from '../../contexts/oauthConfig';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

/**
 * Handles the redirect back from redirect-based OAuth providers (Microsoft,
 * Facebook, GitHub, LinkedIn, X, Amazon). Reads the code or id_token from the
 * URL, validates state, and completes login via the backend.
 *
 * Google does NOT use this page (it uses the GSI popup on the login form).
 */
const OAuthCallback = () => {
  const { loginWithProvider } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against StrictMode double-invoke
    ran.current = true;

    const complete = async () => {
      const provider = sessionStorage.getItem('oauth_provider');
      const savedState = sessionStorage.getItem('oauth_state');
      const cfg = provider ? PROVIDERS[provider] : null;

      if (!provider || !cfg) {
        setError('Unknown or missing provider in callback.');
        return;
      }

      // Params may arrive in the query string (code flow) or hash (id_token).
      const query = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const returnedState = query.get('state') || hash.get('state');
      const providerError = query.get('error') || hash.get('error');

      if (providerError) {
        setError(`Provider returned an error: ${providerError}`);
        return;
      }
      if (savedState && returnedState && savedState !== returnedState) {
        setError('State mismatch — possible CSRF. Login aborted.');
        return;
      }

      let payload = null;
      if (cfg.responseType && cfg.responseType.includes('id_token')) {
        const idToken = hash.get('id_token') || query.get('id_token');
        if (!idToken) {
          setError('No ID token returned by provider.');
          return;
        }
        payload = { credential: idToken };
      } else {
        const code = query.get('code') || hash.get('code');
        if (!code) {
          setError('No authorization code returned by provider.');
          return;
        }
        payload = { code, redirectUri: OAUTH_REDIRECT_URI };
        if (cfg.codeVerifier) payload.codeVerifier = cfg.codeVerifier;
      }

      const result = await loginWithProvider(provider, payload);
      sessionStorage.removeItem('oauth_provider');
      sessionStorage.removeItem('oauth_state');

      if (result.success) {
        navigate('/strategy', { replace: true });
      } else {
        setError(result.error || 'Login failed.');
      }
    };

    complete();
  }, [loginWithProvider, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        {error ? (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign-in failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="btn-primary"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Completing sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
